import { deDE, enUS } from "@mui/material/locale";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import defaultI18NInstance from "i18next";
import React from "react";
import { I18nextProvider } from "react-i18next";
import { LxCircularProgress } from "../LoadingContent";
import { Parent } from "../Types";
import { lxSwitch } from "../Utils";
import { LxI18NProvider, useLxI18NProvider } from "./internal/LxI18N.context";
import { LxI18NProps } from "./LxI18N.model";

function InternalI18NProvider(props: Parent & { testMode?: boolean }) {
  const { i18nInstance, status } = useLxI18NProvider();

  if (status === "initializing" && !props.testMode) {
    return <LxCircularProgress size="large" />;
  }

  return (
    <I18nextProvider i18n={i18nInstance ?? defaultI18NInstance}>
      {props.children}
    </I18nextProvider>
  );
}

function ThemeWithLocalization(props: Parent) {
  const { currentLocale } = useLxI18NProvider();
  const currentLang = currentLocale ?? "de-DE";
  const theme = useTheme();

  const muiLocale = lxSwitch(currentLang as any)
    .case(/^de/, deDE)
    .case(/^en/, enUS)
    .default(deDE)
    .get();

  const themeWithLocale = React.useMemo(
    () => createTheme(theme, muiLocale),
    [muiLocale, theme]
  );

  return (
    <ThemeProvider theme={themeWithLocale}>{props.children}</ThemeProvider>
  );
}

/**
 * `LxI18N` provides translation support for your application.
 *
 * It is a wrapper around the `i18next` library.
 *
 * Use it together with:
 * - the `makeLxTranslations` function to create a translation bundle
 * - the `lxo-i18n-extract` tool to extract translations from your application
 * - the `useLxTranslations` hook to use the translation bundle.
 *
 * ---
 *
 * ⚠️ Note: You cannot use `LxI18N` with `lxSetupTranslations`.
 *   Using `lxSetupTranslations` will disable the `LxI18N` backend
 *   plugin for `i18n`.
 *
 * ---
 *
 * @package I18N
 * @example
 *
 * // Put this in your App.tsx (somewhere on the top of your app)
 * // The `resolver` function and the `NAMESPACES` array will be
 * // created by the `lxo-i18n-extract` tool.
 * <LxI18N resolver={[resolver]} namespaces={NAMESPACES} locale="de-DE">
 *  <App />
 * </LxI18N>
 *
 * // Create translation bundles in your Component:
 * const MY_TRANSLATIONS = makeLxTranslations({
 *  translations: {
 *   "hello": "Hallo",
 *   "world": "Welt"
 *  }
 * });
 *
 * // ... and use it like with the `useLxTranslations` hook.
 * function MyComponent() {
 *   const { t } = useLxTranslations(MY_TRANSLATIONS);
 *
 *   return <div>{t("hello")} {t("world")}</div>;
 * }
 *
 * // To extract translations from your application, and to create the
 * // resolver functions use the `lxo-i18n-extract` tool from the `@lxo-blocs/react`
 * // package
 *
 */
export function LxI18N(props: LxI18NProps) {
  return (
    <LxI18NProvider config={props}>
      <InternalI18NProvider testMode={props.testMode}>
        <ThemeWithLocalization>{props.children}</ThemeWithLocalization>
      </InternalI18NProvider>
    </LxI18NProvider>
  );
}
